import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { socialApi } from '../../utils/api';
import {
    Share2,
    Plus,
    Loader2,
    Sparkles,
    Calendar,
    Send,
    Image,
    Twitter,
    Linkedin,
    Instagram,
    Facebook,
    Clock,
    Heart,
    MessageCircle,
    Repeat,
    Trash2,
    Edit
} from 'lucide-react';

export default function SocialModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [showGenerator, setShowGenerator] = useState(false);
    const [platform, setPlatform] = useState('twitter');
    const [postTopic, setPostTopic] = useState('');
    const [postTone, setPostTone] = useState('professional');

    // Fetch posts
    const { data: postsData, isLoading } = useQuery({
        queryKey: ['social-posts', projectId],
        queryFn: () => socialApi.listPosts(projectId!),
        enabled: !!projectId
    });

    // Generate post mutation
    const generatePostMutation = useMutation({
        mutationFn: (data: any) => socialApi.generatePost(projectId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['social-posts', projectId] });
            setShowGenerator(false);
            setPostTopic('');
        }
    });

    // Publish post mutation
    const publishPostMutation = useMutation({
        mutationFn: (postId: string) => socialApi.publishPost(postId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['social-posts', projectId] });
        }
    });

    // Delete post mutation
    const deletePostMutation = useMutation({
        mutationFn: (postId: string) => socialApi.deletePost(postId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['social-posts', projectId] });
        }
    });

    const posts = postsData?.data?.posts || [];

    const getPlatformIcon = (p: string) => {
        switch (p) {
            case 'twitter': return Twitter;
            case 'linkedin': return Linkedin;
            case 'instagram': return Instagram;
            case 'facebook': return Facebook;
            default: return Share2;
        }
    };

    const getPlatformColor = (p: string) => {
        switch (p) {
            case 'twitter': return 'text-sky-400 bg-sky-500/20';
            case 'linkedin': return 'text-blue-400 bg-blue-500/20';
            case 'instagram': return 'text-pink-400 bg-pink-500/20';
            case 'facebook': return 'text-blue-500 bg-blue-600/20';
            default: return 'text-primary-400 bg-primary-500/20';
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'published':
                return 'bg-green-500/20 text-green-400';
            case 'scheduled':
                return 'bg-blue-500/20 text-blue-400';
            case 'draft':
                return 'bg-dark-700 text-dark-400';
            default:
                return 'bg-dark-700 text-dark-400';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Share2 className="w-8 h-8 text-pink-400" />
                        Social Media
                    </h1>
                    <p className="text-dark-400 mt-1">Create, schedule, and manage social media content</p>
                </div>
                <button
                    onClick={() => setShowGenerator(true)}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <Sparkles className="w-4 h-4" />
                    Generate Post
                </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Posts', value: posts.length, icon: Share2, color: 'text-primary-400' },
                    { label: 'Published', value: posts.filter((p: any) => p.status === 'published').length, icon: Send, color: 'text-green-400' },
                    { label: 'Scheduled', value: posts.filter((p: any) => p.status === 'scheduled').length, icon: Clock, color: 'text-blue-400' },
                    { label: 'Drafts', value: posts.filter((p: any) => p.status === 'draft').length, icon: Edit, color: 'text-yellow-400' }
                ].map((stat) => (
                    <div key={stat.label} className="card p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-dark-400 text-sm">{stat.label}</p>
                                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                            </div>
                            <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Generator Modal */}
            {showGenerator && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-dark-900 rounded-2xl p-6 w-full max-w-lg border border-dark-800">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary-400" />
                            AI Post Generator
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Platform</label>
                                <div className="flex gap-2">
                                    {['twitter', 'linkedin', 'instagram', 'facebook'].map((p) => {
                                        const Icon = getPlatformIcon(p);
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => setPlatform(p)}
                                                className={`p-3 rounded-lg border transition-colors ${platform === p
                                                    ? 'border-primary-500 bg-primary-500/10'
                                                    : 'border-dark-700 hover:border-dark-600'
                                                    }`}
                                            >
                                                <Icon className={`w-5 h-5 ${platform === p ? 'text-primary-400' : 'text-dark-400'}`} />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="label">Topic / Subject</label>
                                <textarea
                                    value={postTopic}
                                    onChange={(e) => setPostTopic(e.target.value)}
                                    className="input h-24 resize-none"
                                    placeholder="What should the post be about?"
                                />
                            </div>

                            <div>
                                <label className="label">Tone</label>
                                <select
                                    value={postTone}
                                    onChange={(e) => setPostTone(e.target.value)}
                                    className="input"
                                >
                                    <option value="professional">Professional</option>
                                    <option value="casual">Casual</option>
                                    <option value="humorous">Humorous</option>
                                    <option value="inspiring">Inspiring</option>
                                    <option value="educational">Educational</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowGenerator(false)}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => generatePostMutation.mutate({ platform, topic: postTopic, tone: postTone })}
                                disabled={!postTopic || generatePostMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {generatePostMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Posts List */}
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                </div>
            ) : posts.length === 0 ? (
                <div className="card p-12 text-center">
                    <Share2 className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
                    <p className="text-dark-400 mb-4">Create your first social media post with AI</p>
                    <button
                        onClick={() => setShowGenerator(true)}
                        className="btn btn-primary inline-flex items-center gap-2"
                    >
                        <Sparkles className="w-4 h-4" />
                        Generate First Post
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {posts.map((post: any) => {
                        const PlatformIcon = getPlatformIcon(post.platform);
                        return (
                            <div key={post.id} className="card p-6">
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-xl ${getPlatformColor(post.platform)}`}>
                                        <PlatformIcon className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(post.status)}`}>
                                                {post.status}
                                            </span>
                                            <span className="text-dark-500 text-sm">
                                                {new Date(post.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-white whitespace-pre-wrap">{post.content}</p>

                                        {post.status === 'published' && (
                                            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-dark-800 text-sm text-dark-400">
                                                <span className="flex items-center gap-1">
                                                    <Heart className="w-4 h-4" />
                                                    {post.likes || 0}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <MessageCircle className="w-4 h-4" />
                                                    {post.comments || 0}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Repeat className="w-4 h-4" />
                                                    {post.shares || 0}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {post.status === 'draft' && (
                                            <button
                                                onClick={() => publishPostMutation.mutate(post.id)}
                                                disabled={publishPostMutation.isPending}
                                                className="btn btn-primary btn-sm flex items-center gap-1"
                                            >
                                                <Send className="w-3 h-3" />
                                                Publish
                                            </button>
                                        )}
                                        <button
                                            onClick={() => deletePostMutation.mutate(post.id)}
                                            className="p-2 text-dark-400 hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
